"use client";

import { useAppDispatch } from "@/hooks/useAppSelector";
import { setFirstLogin } from "@/store/slices/authSlice";
import React, { useState } from "react";
import Joyride, { STATUS, CallBackProps, Step } from "react-joyride";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";

const TourGuide: React.FC = () => {
  const dispatch = useAppDispatch();
  const [run, setRun] = useState<boolean>(true);

  const steps: Step[] = [
    {
      target: ".shop-by-brand",
      title: "Shop By Brand",
      content: "Select your brand from this tab.",
      disableBeacon: true,
    },
    {
      target: ".filter-section",
      title: "Filter By Category",
      content: "Use filters to narrow down your product search.",
      disableBeacon: true,
    },
    {
      target: ".build-your-cart",
      title: "Build Your Cart",
      content: "Start adding items to reach MOQ and unlock amazing discounts.",
      disableBeacon: true,
    },
    {
      target: ".product-table",
      title: "Product Table",
      content: "View all products, pricing, and add items to your cart.",
      disableBeacon: true,
    },
    {
      target: ".open-sidebar",
      title: "View Product Detail",
      content: "Click here to view product Details.",
      disableBeacon: true,
    },
    {
      target: ".normal-pricing",
      title: "Standard Pricing",
      content: "This price applies when you buy less than the MOQ quantity.",
      disableBeacon: true,
    },
    {
      target: ".bulk-pricing",
      title: "Bulk Pricing",
      content: "Enjoy special discounted rates when you order MOQ quantity or more!",
      disableBeacon: true,
    },
  ];

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status } = data;
    const allowedStatuses = [STATUS.FINISHED, STATUS.SKIPPED] as const;
    if (allowedStatuses.includes(status as (typeof allowedStatuses)[number])) {
      setRun(false);
      dispatch(setFirstLogin(false));
    }
  };

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      showSkipButton={false}
      spotlightClicks={true}
      disableOverlayClose={false}
      showProgress={false}
      scrollOffset={64}
      scrollToFirstStep
      callback={handleJoyrideCallback}
      styles={{
        options: {
          zIndex: 10000,
          arrowColor: "transparent",
          primaryColor: "#28a447",
          textColor: "black",
          backgroundColor: "white",
          overlayColor: "rgba(0, 0, 0, 0.7)",
        },
        tooltip: {
          borderRadius: "10px",
          padding: "20px",
          boxShadow: "0px 6px 14px rgba(0, 0, 0, 0.50)",
          width: "320px",
          minHeight: "150px",
          position: "relative",
        },
      }}
      locale={{
        next: "Next",
      }}
      floaterProps={{
        placement: "top",
      }}
      tooltipComponent={({ index, step, primaryProps, backProps }) => (
        <div className="p-3 bg-white-1 rounded-sm w-[250px] min-h-[130px] flex flex-col justify-between">
          {/* Step Indicator */}
          <div className="text-xs text-gray-500 font-medium">
            Step {index + 1}/{steps.length}
          </div>

          {/* Title */}
          <h3 className="text-base font-bold text-gray-900 mt-1">
            {step?.title}
          </h3>

          {/* Step Content */}
          <p className="text-gray-600 text-sm mt-0">{step.content}</p>

          {/* Navigation Controls */}
          <div className="flex justify-between items-center mt-2">
            <button
              onClick={() => dispatch(setFirstLogin(false))}
              className="text-sm text-gray-500 hover:underline"
            >
              Skip
            </button>
            <div className="flex items-center justify-center">
              <button
                {...backProps}
                className={`${
                  index === 0 ? "opacity-0 pointer-events-none" : ""
                } text-sm text-gray-500 hover:text-red-500`}
              >
                <ArrowLeftIcon className="text-gray-400 -mr-1" />
              </button>
              {/* Pagination Dots */}
              <div className="flex items-center gap-1">
                {steps.map((_, i) => (
                  <div
                    key={i}
                    className={`w-2 h-2 rounded-full transition-all ${
                      i === index ? "bg-gray-800" : "bg-gray-300"
                    }`}
                  />
                ))}
              </div>
              <button
                {...primaryProps}
                className="text-sm text-gray-500 hover:text-green-500"
              >
                <ArrowRightIcon className="text-gray-400 -ml-1" />
              </button>
            </div>

            {/* Next Button */}
            <button
              {...primaryProps}
              className="bg-[#28a447] text-white-1 text-sm px-4 py-1 rounded-[3px]"
            >
              {index === steps.length - 1 ? "Finish" : "Next"}
            </button>
          </div>
        </div>
      )}
    />
  );
};

export default TourGuide;
